export const FREE_MONTHLY_MINUTES = 20;
export const PAID_MONTHLY_HOURS = 30;
export const TOPUP_PACK_HOURS = 10;
export const PAID_PRICE_LABEL = "$9+vat /mo";
export const TOPUP_PRICE_LABEL = "$3+vat";

const parseFiniteNumber = (value) => {
    if (
        value === null ||
        value === undefined ||
        (typeof value === "string" && value.trim() === "")
    ) {
        return null;
    }
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
};

const toFiniteNumber = (value, fallback = 0) => {
    const number = parseFiniteNumber(value);
    return number === null ? fallback : number;
};

export const getLimitMessaging = (entitlement) => {
    const planCode = entitlement?.plan_code || "free";
    const paidLabel = entitlement?.pricing?.paid_label || PAID_PRICE_LABEL;
    const topupLabel = entitlement?.pricing?.topup_label || TOPUP_PRICE_LABEL;

    if (planCode === "captions_paid") {
        const totalHours = toFiniteNumber(
            entitlement?.monthly_paid_hours_total,
            PAID_MONTHLY_HOURS
        );
        const usedHours = parseFiniteNumber(
            entitlement?.monthly_paid_hours_used
        );
        const topupBalance = parseFiniteNumber(
            entitlement?.topup_hours_balance
        );
        const hasKnownUsage = usedHours !== null && topupBalance !== null;
        return {
            title: "Monthly limit reached",
            body: `You've used your included ${totalHours} hours and available top-up balance.`,
            details: hasKnownUsage
                ? `${usedHours.toFixed(
                      2
                  )}h / ${totalHours}h used. Top-up balance: ${topupBalance.toFixed(
                      2
                  )}h.`
                : null,
            ctaLabel: `Buy ${TOPUP_PACK_HOURS} extra hours (${topupLabel})`,
            actionType: "topup",
        };
    }

    const totalMinutes = toFiniteNumber(
        entitlement?.monthly_free_minutes_total,
        FREE_MONTHLY_MINUTES
    );
    const rawUsedMinutes = entitlement?.monthly_free_minutes_used;
    const usedMinutes = parseFiniteNumber(rawUsedMinutes);
    const hasKnownUsage = usedMinutes !== null;
    // Top-up packs are subscriber-only; a downgraded user's remaining balance
    // is frozen until they subscribe again, so the only CTA is upgrading.
    const frozenTopupHours = Math.max(
        0,
        toFiniteNumber(entitlement?.topup_hours_balance)
    );

    return {
        title: "Free trial exhausted",
        body:
            `You've used your free ${totalMinutes} minutes for this month.` +
            (frozenTopupHours > 0
                ? ` Your ${frozenTopupHours.toFixed(
                      2
                  )}h top-up balance unlocks when you subscribe.`
                : ""),
        details: hasKnownUsage
            ? `${usedMinutes.toFixed(1)} / ${totalMinutes.toFixed(
                  1
              )} minutes used.`
            : null,
        ctaLabel: `Upgrade to paid (${paidLabel})`,
        actionType: "upgrade",
    };
};

const formatMeterHours = (hours) => {
    const rounded = Math.round(toFiniteNumber(hours) * 10) / 10;
    return Number.isInteger(rounded) ? `${rounded}` : rounded.toFixed(1);
};

export const getUsageMeter = (entitlement) => {
    if (!entitlement) return null;
    const planCode = entitlement.plan_code || "free";

    if (planCode === "captions_paid") {
        const hoursLeft = Math.max(
            0,
            toFiniteNumber(entitlement.monthly_paid_hours_remaining)
        );
        const topup = Math.max(
            0,
            toFiniteNumber(entitlement.topup_hours_balance)
        );
        const total = toFiniteNumber(
            entitlement.monthly_paid_hours_total,
            PAID_MONTHLY_HOURS
        );
        const fraction =
            total > 0 ? Math.max(0, Math.min(1, hoursLeft / total)) : 0;
        let label = `${formatMeterHours(hoursLeft)} h remaining`;
        if (topup > 0) label += ` + ${formatMeterHours(topup)}h top-up`;
        return { label, fraction, low: hoursLeft <= 0.5 && topup <= 0 };
    }

    const secondsLeft = Math.max(
        0,
        toFiniteNumber(entitlement.monthly_free_seconds_remaining)
    );
    const totalSeconds = toFiniteNumber(
        entitlement.monthly_free_seconds_total,
        FREE_MONTHLY_MINUTES * 60
    );
    if (totalSeconds <= 0) return null;
    // A downgraded user's top-up balance is frozen until they resubscribe,
    // so the free meter only counts the trial minutes they can actually use.
    const minutesLeft = Math.ceil(secondsLeft / 60);
    const fraction = Math.max(0, Math.min(1, secondsLeft / totalSeconds));
    return {
        label: `${minutesLeft} min remaining this month`,
        fraction,
        low: secondsLeft <= 120,
    };
};
